import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { api } from '@/services/api'
import type { ApiEnvelope, Membership, User } from '@/types'

type AuthPayload = { user: User; memberships: Membership[] }

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const memberships = ref<Membership[]>([])
  const initialized = ref(false)
  const authenticated = computed(() => user.value !== null)
  const isSuperAdmin = computed(() => user.value?.is_super_admin === true)

  function apply(payload: AuthPayload) {
    user.value = payload.user
    memberships.value = payload.memberships
    initialized.value = true
  }

  async function fetchMe() {
    try {
      const response = await api<ApiEnvelope<AuthPayload>>('/me')
      apply(response.data)
    } catch {
      user.value = null
      memberships.value = []
      initialized.value = true
    }
  }

  async function login(credentials: { email: string; password: string }) {
    const response = await api<ApiEnvelope<AuthPayload>>('/auth/login', { method: 'POST', body: JSON.stringify(credentials) })
    apply(response.data)
    if (user.value?.is_super_admin) return '/super-admin/dashboard'
    const slug = memberships.value[0]?.pharmacy?.slug
    return slug ? `/${slug}/dashboard` : '/login'
  }

  async function register(payload: Record<string, unknown>) {
    const response = await api<ApiEnvelope<{ user: User; pharmacy: { slug: string } }>>('/auth/register-pharmacy', { method: 'POST', body: JSON.stringify(payload) })
    await fetchMe()
    return response.data.pharmacy.slug
  }

  async function logout() {
    await api('/auth/logout', { method: 'POST' })
    user.value = null
    memberships.value = []
  }

  function membershipFor(slug: string) {
    return memberships.value.find((membership) => membership.pharmacy?.slug === slug)
  }

  return { user, memberships, initialized, authenticated, isSuperAdmin, fetchMe, login, register, logout, membershipFor }
})
